import { MapPin } from "lucide-mui"
import React from "react"
import ReactMarkdown from "react-markdown"

import type { VenuePlanData } from "@/components/plan"
import { PlanViewer } from "@/components/plan"
import BookingPolicy from "@/components/public/venue/booking_policy"
import { publicSectionClasses } from "@/components/public/venue/section_styles"
import type { PublicReservationRuleSection, PublicVenueQuery } from "@/graphql/graphql"
import i18n from "@/i18n"
import type { BookingPolicyTileViewModel } from "@/shared/booking_policy"
import useIsMobile from "@/shared/use_is_mobile"

const PLAN_FIT_PADDING_MOBILE = 16

const STYLES = {
  title: "mb-4 mt-0 text-base font-medium text-[#222222]",
  description: "text-gray-700 leading-relaxed",
  planPreview: "mt-4 h-[300px] md:h-[400px] border border-gray-200 rounded-lg overflow-hidden",
  planPreviewMobile: "-mx-4 mt-4 aspect-[3/4] overflow-hidden",
  planPromo: "mt-4 flex items-center gap-3 rounded-[10px] border border-dashed border-[#d4dbe3] bg-[#fafbfc] p-3",
  planPromoIcon: "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#e9f5ff]",
  planPromoTitle: "text-[13px] font-medium leading-[18px] text-[#111827]",
  planPromoSubtitle: "mt-[2px] text-[11px] leading-[15px] text-[#838fa0]",
  planPromoButton:
    "ml-auto h-[30px] shrink-0 cursor-pointer rounded-[6px] border-0 bg-[#2596fa] px-4 text-[12px] font-medium text-white",
  markdown:
    "prose prose-sm max-w-none text-[12px] leading-[18px] text-[#5f6774] prose-p:my-0 prose-ul:my-0 prose-li:my-0 prose-strong:text-[#444] prose-strong:font-semibold",
} as const

type VenueRuleSectionProps = {
  section: PublicReservationRuleSection
  venue: NonNullable<PublicVenueQuery["public"]["venue"]>
  bookingPolicyTiles: BookingPolicyTileViewModel[]
  plansWithContent: VenuePlanData[]
  onBookTable?: () => void
  showDivider?: boolean
}

const SectionContent: React.FC<{ content?: string | null }> = ({ content }) => {
  if (!content?.trim()) return null

  return (
    <div className={STYLES.markdown}>
      <ReactMarkdown>{content}</ReactMarkdown>
    </div>
  )
}

const VenueRuleSection: React.FC<VenueRuleSectionProps> = ({
  section,
  venue,
  bookingPolicyTiles,
  plansWithContent,
  onBookTable,
  showDivider = true,
}) => {
  const isMobile = useIsMobile()
  const wrapperClasses = publicSectionClasses(isMobile, showDivider)

  if (section.identifier === "about_venue") {
    const description = venue.info?.description
    if (!description && !section.content?.trim()) return null

    return (
      <div className={wrapperClasses} data-testid={`rule-section-${section.identifier}`}>
        <h3 className={STYLES.title}>{section.title}</h3>
        {!!description && (
          <p className={STYLES.description} data-testid="public-venue-description-text">
            {description}
          </p>
        )}
        <SectionContent content={section.content} />
      </div>
    )
  }

  if (section.identifier === "venue_plan") {
    const hasPlan = plansWithContent.length > 0
    if (!hasPlan && !section.content?.trim()) return null

    return (
      <div className={wrapperClasses} data-testid={`rule-section-${section.identifier}`}>
        <h3 className={STYLES.title}>{section.title}</h3>
        <SectionContent content={section.content} />
        {!!hasPlan && (
          <div
            className={isMobile ? STYLES.planPreviewMobile : STYLES.planPreview}
            data-testid="public-venue-plan-preview"
          >
            <PlanViewer
              venuePlans={plansWithContent}
              tables={[]}
              fitPadding={isMobile ? PLAN_FIT_PADDING_MOBILE : 0}
              frameless={isMobile}
            />
          </div>
        )}
        {!!hasPlan && !!isMobile && !!onBookTable && (
          <div className={STYLES.planPromo} data-testid="public-venue-plan-promo">
            <div className={STYLES.planPromoIcon}>
              <MapPin className="h-5 w-5 text-[#2596fa]" />
            </div>
            <div className="min-w-0">
              <div className={STYLES.planPromoTitle}>{i18n.t("app.public.venue.plan_promo.title")}</div>
              <div className={STYLES.planPromoSubtitle}>{i18n.t("app.public.venue.plan_promo.subtitle")}</div>
            </div>
            <button
              type="button"
              onClick={onBookTable}
              className={STYLES.planPromoButton}
              data-testid="public-venue-plan-promo-book"
            >
              {i18n.t("app.public.venue.plan_promo.button")}
            </button>
          </div>
        )}
      </div>
    )
  }

  if (section.identifier === "booking_policy") {
    return (
      <BookingPolicy
        tiles={bookingPolicyTiles}
        title={section.title}
        content={section.content}
        testIdPrefix="public-booking-policy"
        cardTiles={isMobile}
        wrapperClassName={wrapperClasses}
      />
    )
  }

  if (!section.content?.trim()) return null

  return (
    <div className={wrapperClasses} data-testid={`rule-section-${section.identifier}`}>
      <h3 className={STYLES.title}>{section.title}</h3>
      <SectionContent content={section.content} />
    </div>
  )
}

export default VenueRuleSection
